import { useTranslation } from 'react-i18next';

import SkeletonBox from '@/components/atoms/SkeletonBox/SkeletonBox';
import { useChartTheme } from '@/components/charts/theme';
import type { Locale } from '@/utils/localeFormatters';
import { formatPercentageByLocale, parseLocale } from '@/utils/localeFormatters';

import styles from './PercentageBars.module.scss';

export interface PercentageBarItem {
    label: string;
    value: number | null;
    color: string;
}

export interface PercentageBarsProps {
    title: string;
    items: PercentageBarItem[];
    decimals?: number;
    isLoading?: boolean;
}

export default function PercentageBars(props: PercentageBarsProps) {
    const { title, items, decimals = 0, isLoading = false } = props;

    const { i18n } = useTranslation();
    const locale: Locale = parseLocale(i18n.language);
    // Item colours are chart palette colours; the bars sit next to charts that lift them in dark.
    const chartTheme = useChartTheme();

    return (
        <div className={styles.container}>
            <div className={styles.title}>{title}</div>
            <div className={styles.bars}>
                {isLoading ? (
                    <SkeletonBox width="100%" height={80} borderRadius={8} />
                ) : (
                    items.map((item, index) => {
                        const formatted = formatPercentageByLocale(item.value, locale, decimals);

                        return (
                            <div key={`${item.label}-${index}`} className={styles.barRow}>
                                <div className={styles.barHeader}>
                                    <span className={styles.barLabel}>{item.label}</span>
                                    <span className={styles.barValue}>{formatted}</span>
                                </div>
                                <div className={styles.barTrack}>
                                    <div
                                        className={styles.barFill}
                                        style={{
                                            width: item.value ? `${item.value}%` : '0%',
                                            backgroundColor: chartTheme.color(item.color),
                                        }}
                                    />
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}
